import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  Badge,
  Button,
  Field,
  Input,
  Textarea,
  LoadingState,
  ErrorState,
  EmptyState,
} from "@/components/design-system";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import {
  listMyApp,
  applyAmbassador,
  rescore,
  publicList,
  inviteAmbassador,
  listSentInvitations,
  listReceivedInvitations,
  respondInvitation,
  listMyProductAmbassadorships,
  listMyReferredSubscriptions,
} from "@/lib/ambassadors";
import { listOwnProducts } from "@/lib/products";

// ---------- Shared UI atoms ------------------------------------------------
function Score({ v }) {
  const color = v >= 80 ? "#7ED957" : v >= 60 ? "#2D7FF9" : v >= 40 ? "#20C7E8" : "#DC2626";
  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-full flex items-center justify-center font-[Montserrat] font-extrabold text-3xl text-white" style={{ background: color }}>{v}</div>
      <div className="text-sm text-[#0B1F3A]/70">AI trust score<br/>(0–100)</div>
    </div>
  );
}

function Stat({ label, v, testId, compact = false, muted = false }) {
  return (
    <div data-testid={testId} className={compact ? "rounded-md bg-white border border-[#0B1F3A]/10 p-2" : "rounded-lg bg-[#E8F1FF] p-3"}>
      <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">{label}</div>
      <div className={`${compact ? "text-sm font-bold mt-0.5" : "text-2xl font-[Montserrat] font-extrabold mt-1"} ${muted ? "text-[#0B1F3A]/40" : "text-[#0B1F3A]"}`}>{v}</div>
    </div>
  );
}

function money(cents, currency = "usd") {
  const n = (Number(cents || 0) / 100);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(n);
}

async function copyText(v) {
  try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
  catch { toast.error("Copy failed"); }
}

const SOURCE_META = {
  helpful: { label: "Helpful feedback", tint: "bg-[#7ED957]/20 border-[#7ED957]/60 text-[#0B1F3A]" },
  invitation: { label: "Founder invitation", tint: "bg-[#2D7FF9]/15 border-[#2D7FF9]/50 text-[#0B1F3A]" },
};

const INV_BADGE = {
  sent: { variant: "neutral", label: "Sent" },
  accepted: { variant: "active", label: "Accepted" },
  declined: { variant: "admin", label: "Declined" },
};

// ---------- Application / status dashboard --------------------------------
function ApplicationStatusDashboard({ user, application }) {
  const points = user?.reputation_points ?? 0;
  const POINTS_GOAL = 100;
  const progressPct = Math.min(100, Math.round((points / POINTS_GOAL) * 100));

  const status = application?.status || (user?.ambassador_status === "verified" ? "verified" : "not_applied");
  const label = {
    verified: { text: "Verified Ambassador", tint: "bg-[#7ED957]/20 text-[#0B1F3A] border-[#7ED957]/60" },
    approved: { text: "Verified Ambassador", tint: "bg-[#7ED957]/20 text-[#0B1F3A] border-[#7ED957]/60" },
    scoring: { text: "AI scoring in progress", tint: "bg-[#20C7E8]/20 text-[#0B1F3A] border-[#20C7E8]/60" },
    scored: { text: "Awaiting admin review", tint: "bg-[#2D7FF9]/15 text-[#0B1F3A] border-[#2D7FF9]/60" },
    rejected: { text: "Application rejected", tint: "bg-[#DC2626]/15 text-[#0B1F3A] border-[#DC2626]/60" },
    score_failed: { text: "Rescore needed", tint: "bg-[#DC2626]/15 text-[#0B1F3A] border-[#DC2626]/60" },
    not_applied: { text: "Not applied yet", tint: "bg-[#E8F1FF] text-[#0B1F3A] border-[#0B1F3A]/15" },
  }[status] || { text: status, tint: "bg-[#E8F1FF] text-[#0B1F3A] border-[#0B1F3A]/15" };

  const step = (done, muted, title, sub) => (
    <li className={`flex gap-3 items-start ${muted && !done ? "opacity-60" : ""}`}>
      <span
        className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-[#7ED957] text-[#0B1F3A]" : "bg-white border-2 border-[#0B1F3A]/15 text-[#0B1F3A]/50"}`}
      >
        {done ? "✓" : "•"}
      </span>
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${done ? "text-[#0B1F3A]" : "text-[#0B1F3A]/80"}`}>{title}</div>
        {sub && <div className="text-xs text-[#0B1F3A]/60 mt-0.5">{sub}</div>}
      </div>
    </li>
  );

  const hasPoints = points >= POINTS_GOAL;
  const hasApplied = !!application && status !== "not_applied";
  const scoreOk = application?.score && application.score >= 60;
  const isVerified = status === "verified" || status === "approved";

  return (
    <Card data-testid="ambassador-status-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#0B1F3A]/60">Your status</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${label.tint}`} data-testid="ambassador-status-badge">
              {label.text}
            </span>
            {application?.score !== undefined && application?.score !== null && (
              <span className="text-xs text-[#0B1F3A]/65">AI score: <b className="text-[#0B1F3A]">{application.score}</b>/100</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/55">Reputation</div>
          <div className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A]">{points} <span className="text-sm text-[#0B1F3A]/50 font-semibold">pts</span></div>
        </div>
      </div>

      {!isVerified && (
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[#0B1F3A]/70 mb-1.5">
            <span>Path to Ambassador</span>
            <span><b className="text-[#0B1F3A]">{points}</b> / {POINTS_GOAL} pts</span>
          </div>
          <div className="w-full h-2 rounded-full bg-[#E8F1FF] overflow-hidden">
            <div
              className={`h-full transition-all ${hasPoints ? "bg-[#7ED957]" : "bg-[#2D7FF9]"}`}
              style={{ width: `${progressPct}%` }}
              data-testid="ambassador-points-progress"
            />
          </div>
        </div>
      )}

      <ul className="mt-5 space-y-3" data-testid="ambassador-checklist">
        {step(hasPoints, false, `Reach ${POINTS_GOAL} reputation points`, hasPoints ? "Done — you're at the ambassador_eligible tier." : `Give ${POINTS_GOAL - points} more helpful feedback to unlock the application.`)}
        {step(hasApplied, !hasPoints, "Submit your application", hasApplied ? `Submitted — status: ${label.text.toLowerCase()}.` : "Fill in your motivation, audience and channels below.")}
        {step(!!scoreOk, !hasApplied, "AI score ≥ 60", scoreOk ? `Passed with ${application.score}/100.` : application?.score != null ? `Your last score: ${application.score}/100. Rescore below.` : "AI reviews your application and gives a trust score.")}
        {step(isVerified, !scoreOk, "Admin approves your application", isVerified ? "You're a Verified Ambassador — dashboard unlocked above." : "Once approved, per-product referral codes are minted automatically.")}
      </ul>
    </Card>
  );
}

function ApplicationForm({ onSubmit, busy, error }) {
  const [form, setForm] = useState({ motivation: "", audience: "", channels: "", links: "" });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const links = form.links.split(/\s+/).filter(Boolean).slice(0, 6);
        onSubmit({ motivation: form.motivation, audience: form.audience, channels: form.channels, links });
      }}
      className="space-y-4"
      data-testid="ambassador-apply-form"
    >
      <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Apply to become a Verified Ambassador</h2>
      <Field label="Why do you want to be an ambassador?" hint="≥40 chars"><Textarea data-testid="a-motivation" minLength={40} maxLength={2000} rows={4} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} required /></Field>
      <Field label="Who is your audience?" hint="Who do you reach that SaaS founders would care about?"><Textarea data-testid="a-audience" minLength={20} maxLength={1000} rows={2} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} required /></Field>
      <Field label="Distribution channels" hint="LinkedIn, newsletter, community, IRL events…"><Input data-testid="a-channels" minLength={10} maxLength={500} value={form.channels} onChange={(e) => setForm({ ...form, channels: e.target.value })} required /></Field>
      <Field label="Public links (space-separated, max 6)"><Input data-testid="a-links" value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} placeholder="https://linkedin.com/in/… https://twitter.com/…" /></Field>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button data-testid="ambassador-submit" type="submit" variant="primary" disabled={busy}>{busy ? "Submitting…" : "Apply — get AI-scored"}</Button>
    </form>
  );
}

function ApplicationDetailCard({ application, onRescore, busy }) {
  return (
    <Card data-testid="ambassador-status-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={application.status === "approved" ? "active" : application.status === "rejected" ? "admin" : "neutral"}>{application.status}</Badge>
        <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Applied {new Date(application.created_at).toLocaleDateString()}</span>
      </div>
      <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-3">Your application</h2>
      {application.status === "scoring" && <p className="mt-3 text-sm text-[#0B1F3A]/70">Scoring… takes 5–15 seconds.</p>}
      {typeof application.ai_score === "number" && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Score v={application.ai_score} />
          <div className="md:col-span-2">
            {application.ai_score_rationale && <p className="text-sm text-[#0B1F3A]/85"><b>Why this score:</b> {application.ai_score_rationale}</p>}
            {application.ai_strengths?.length > 0 && (<div className="mt-3"><div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Strengths</div><ul className="mt-1 text-sm text-[#0B1F3A]/85 space-y-1">{application.ai_strengths.map((s, i) => <li key={i}>· {s}</li>)}</ul></div>)}
            {application.ai_concerns?.length > 0 && (<div className="mt-3"><div className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Concerns</div><ul className="mt-1 text-sm text-[#0B1F3A]/85 space-y-1">{application.ai_concerns.map((s, i) => <li key={i}>· {s}</li>)}</ul></div>)}
            <div className="mt-3 text-xs text-[#0B1F3A]/55">Model: {application.model_used} · ${application.cost_usd?.toFixed(4)}</div>
          </div>
        </div>
      )}
      {application.status === "approved" && <p className="mt-4 text-sm text-[#0B1F3A]/80">Approved on {new Date(application.decided_at).toLocaleDateString()} — reload to see your ambassador badge.</p>}
      {application.status === "rejected" && (<div className="mt-4 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-800">Not approved. {application.admin_notes}</div>)}
      {application.status === "scored" && (<div className="mt-6 flex gap-2"><Button data-testid="ambassador-rescore" variant="secondary" onClick={onRescore} disabled={busy}>Rescore</Button><span className="self-center text-xs text-[#0B1F3A]/60">Admin review pending.</span></div>)}
    </Card>
  );
}

// ---------- Preview / locked state -----------------------------------------
function LockedPreviewCard() {
  return (
    <Card data-testid="ambassador-locked-preview" className="bg-gradient-to-br from-[#E8F1FF]/60 to-white border-dashed">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#2D7FF9]/10 flex items-center justify-center text-[#2D7FF9] font-bold">🔒</div>
        <div>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Preview — unlocks after verification</h3>
          <p className="text-sm text-[#0B1F3A]/75 mt-1">
            Once you complete the checklist above, this dashboard becomes live: unique referral links per product,
            real-time clicks and signups, and a subscriber list showing every earning you accrue at <b>12.5%</b> for 12 months.
          </p>
        </div>
      </div>
    </Card>
  );
}

// ---------- Impact stats ---------------------------------------------------
function ImpactStats({ totals, productsCount, muted = false }) {
  return (
    <Card data-testid="ambassador-totals">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Your ambassador impact</h2>
        {muted && (
          <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">Preview · locked</span>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat muted={muted} label="Products" v={productsCount} testId="stat-products" />
        <Stat muted={muted} label="Clicks" v={totals?.clicks || 0} testId="stat-clicks" />
        <Stat muted={muted} label="Signups converted" v={totals?.signups_converted || 0} testId="stat-signups" />
        <Stat muted={muted} label="Earnings" v={money(totals?.ambassador_cents || 0, totals?.currency)} testId="stat-earnings" />
      </div>
    </Card>
  );
}

// ---------- Referred subscribers drawer ------------------------------------
function SubscribersDrawer({ productId, currency = "usd" }) {
  const [state, setState] = useState({ status: "idle", items: [], totals: null, err: "" });
  const [open, setOpen] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const { items, totals } = await listMyReferredSubscriptions(productId);
      setState({ status: "ready", items, totals, err: "" });
    } catch (err) { setState({ status: "error", items: [], totals: null, err: formatApiError(err) }); }
  };
  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && state.status === "idle") await load();
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        data-testid={`amb-subs-toggle-${productId}`}
        className="text-xs font-bold uppercase tracking-widest text-[#2D7FF9] hover:text-[#0B1F3A]"
      >
        {open ? "Hide referred subscribers ▲" : "Show referred subscribers ▼"}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-[#0B1F3A]/10 overflow-hidden" data-testid={`amb-subs-panel-${productId}`}>
          {state.status === "loading" && <div className="p-3 text-xs text-[#0B1F3A]/60">Loading…</div>}
          {state.status === "error" && <div className="p-3 text-xs text-[#DC2626]">{state.err}</div>}
          {state.status === "ready" && state.items.length === 0 && (
            <div className="p-3 text-xs text-[#0B1F3A]/60">
              No paid subscribers yet — you&apos;ll see each one appear here the moment they subscribe through your link.
            </div>
          )}
          {state.status === "ready" && state.items.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-[#E8F1FF] text-[#0B1F3A]/70">
                <tr>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">Subscriber</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wider">Gross</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wider">Your 12.5%</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wider">Since</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#0B1F3A]/5">
                {state.items.map((r) => (
                  <tr key={r.id} className="hover:bg-[#E8F1FF]/40">
                    <td className="px-3 py-2 font-mono text-[#0B1F3A]">{r.subscriber_masked}</td>
                    <td className="px-3 py-2 text-right text-[#0B1F3A]">{money(r.gross_cents, r.currency || currency)}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#2D7FF9]">{money(r.ambassador_cents, r.currency || currency)}</td>
                    <td className="px-3 py-2 text-right text-[#0B1F3A]/70">{new Date(r.period_start || r.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.status === "paid" ? "bg-[#7ED957]/25 text-[#0B1F3A]" : "bg-[#2D7FF9]/15 text-[#0B1F3A]"}`}>
                        {r.status || "accrued"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#E8F1FF]/50">
                <tr>
                  <td className="px-3 py-2 font-bold text-[#0B1F3A]">Total ({state.totals?.subscribers || 0})</td>
                  <td className="px-3 py-2 text-right font-bold text-[#0B1F3A]">{money(state.totals?.gross_cents || 0, currency)}</td>
                  <td className="px-3 py-2 text-right font-bold text-[#2D7FF9]">{money(state.totals?.ambassador_cents || 0, currency)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Products you represent -----------------------------------------
function ProductAmbassadorshipsCard({ enabled }) {
  const [state, setState] = useState({ status: enabled ? "loading" : "ready", items: [], totals: null, err: "" });

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    (async () => {
      try {
        const { items, totals } = await listMyProductAmbassadorships();
        if (mounted) setState({ status: "ready", items, totals, err: "" });
      } catch (err) {
        if (mounted) setState({ status: "error", items: [], totals: null, err: formatApiError(err) });
      }
    })();
    return () => { mounted = false; };
  }, [enabled]);

  const totals = state.totals || {};
  return (
    <>
      <ImpactStats
        totals={totals}
        productsCount={state.items.length}
        muted={!enabled}
      />

      <Card data-testid="ambassador-products-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Products you represent</h2>
          {!enabled && (
            <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">Preview · locked</span>
          )}
        </div>
        <p className="text-sm text-[#0B1F3A]/70 mt-1">
          Each product has its own unique referral link. When someone subscribes to
          that product using your link, you earn <b>12.5%</b> for 12 months.
        </p>

        {!enabled && (
          <div className="mt-4">
            <EmptyState
              title="You'll see your referral codes here once approved."
              description="Get to 100 points, submit your application, and once an admin approves you, unique referral links are minted automatically for every product a founder invites you to (or that you help via feedback marked as Helpful)."
            />
          </div>
        )}

        {enabled && state.status === "loading" && <div className="mt-4"><LoadingState label="Loading…" /></div>}
        {enabled && state.status === "error" && <div className="mt-4"><ErrorState detail={state.err} /></div>}
        {enabled && state.status === "ready" && state.items.length === 0 && (
          <div className="mt-4">
            <EmptyState
              title="No products yet"
              description="You'll appear here once a founder invites you OR marks one of your feedback submissions as Helpful."
            />
          </div>
        )}
        {enabled && state.status === "ready" && state.items.length > 0 && (
          <ul className="mt-4 space-y-4" data-testid="ambassador-products-list">
            {state.items.map((it) => {
              const meta = SOURCE_META[it.source] || { label: it.source, tint: "bg-[#E8F1FF] border-[#0B1F3A]/15 text-[#0B1F3A]" };
              return (
                <li
                  key={it.id}
                  data-testid={`ambassador-product-${it.product_slug || it.product_id}`}
                  className="rounded-xl border border-[#0B1F3A]/10 p-4 hover:border-[#2D7FF9]/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tint}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55">
                          Approved {new Date(it.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="mt-2">
                        {it.product_slug ? (
                          <a href={`/p/${it.product_slug}`} target="_blank" rel="noreferrer" className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] hover:text-[#2D7FF9]">
                            {it.product_name}
                          </a>
                        ) : (
                          <span className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">{it.product_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat compact label="Clicks" v={it.clicks} />
                    <Stat compact label="Pending" v={it.signups_pending} />
                    <Stat compact label="Signups" v={it.signups_converted} />
                    <Stat compact label="Earned" v={money(it.earnings_ambassador_cents)} />
                  </div>
                  <div className="mt-3 rounded-lg bg-[#E8F1FF]/60 border border-[#2D7FF9]/20 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 font-bold">Your referral link</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <code data-testid={`amb-code-${it.product_slug}`} className="text-xs font-mono bg-white px-2 py-1 rounded border border-[#0B1F3A]/15 text-[#0B1F3A] break-all">{it.share_url}</code>
                      <Button variant="secondary" onClick={() => copyText(it.share_url)} data-testid={`amb-copy-${it.product_slug}`}>Copy</Button>
                      <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55">code · <b className="text-[#0B1F3A]">{it.referral_code}</b></span>
                    </div>
                  </div>
                  <SubscribersDrawer productId={it.product_id} currency="usd" />
                  {it.campaign_id && (
                    <div className="mt-3">
                      <Link to={`/campaign/${it.campaign_id}`} className="text-xs text-[#2D7FF9] font-bold uppercase tracking-widest">
                        View campaign ↗
                      </Link>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
}

// ---------- Invitations from founders --------------------------------------
function InvitationsFromFoundersCard({ enabled }) {
  const [state, setState] = useState({ status: enabled ? "loading" : "ready", items: [], err: "" });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const items = await listReceivedInvitations();
      setState({ status: "ready", items, err: "" });
    } catch (err) { setState({ status: "error", items: [], err: formatApiError(err) }); }
  };

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled]);

  const respond = async (id, outcome) => {
    setBusyId(id);
    try { await respondInvitation(id, outcome); toast.success(outcome === "accepted" ? "Invitation accepted." : "Invitation declined."); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  return (
    <Card data-testid="ambassador-invitations-received">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Invitations from founders</h2>
        {!enabled && (
          <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/55 font-bold">Preview · locked</span>
        )}
      </div>
      <p className="text-sm text-[#0B1F3A]/70 mt-1">
        Founders reach out here to ask you to test and promote their apps. Accepting adds it to your programme; your referral link keeps earning you 12.5% for 12 months.
      </p>

      {!enabled && (
        <div className="mt-4">
          <EmptyState
            title="Invitations from founders will appear here."
            description="Founders can only invite Verified Ambassadors. Once your application is approved, invitations land here in real time."
          />
        </div>
      )}

      {enabled && state.status === "loading" && <div className="mt-4"><LoadingState label="Loading…" /></div>}
      {enabled && state.status === "error" && <div className="mt-4"><ErrorState detail={state.err} /></div>}
      {enabled && state.status === "ready" && state.items.length === 0 && (
        <div className="mt-4"><EmptyState title="No invitations yet" description="Once founders start inviting you, they'll show up here." /></div>
      )}
      {enabled && state.status === "ready" && state.items.length > 0 && (
        <div className="mt-4 space-y-3">
          {state.items.map((r) => {
            const meta = INV_BADGE[r.status] || INV_BADGE.sent;
            return (
              <div key={r.id} className="rounded-lg border border-[#0B1F3A]/10 p-4" data-testid={`invitation-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                      <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="mt-2 text-sm text-[#0B1F3A]">
                      From <b>{r.founder?.name || r.founder?.email}</b>
                      {r.product?.name && (<> for <b>{r.product.name}</b></>)}
                    </div>
                    <p className="mt-2 text-sm text-[#0B1F3A]/85 whitespace-pre-wrap">{r.message}</p>
                  </div>
                  {r.status === "sent" && (
                    <div className="flex gap-2">
                      <Button variant="primary" onClick={() => respond(r.id, "accepted")} disabled={busyId === r.id} data-testid={`invite-accept-${r.id}`}>{busyId === r.id ? "…" : "Accept"}</Button>
                      <Button variant="ghost" onClick={() => respond(r.id, "declined")} disabled={busyId === r.id} data-testid={`invite-decline-${r.id}`}>Decline</Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------- Unified Community surface --------------------------------------
function CommunityAmbassadorSurface({ user, refresh }) {
  const [appState, setAppState] = useState({ status: "loading", data: null, err: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isVerified = user?.ambassador_status === "verified";

  const load = async () => {
    try { setAppState({ status: "ready", data: await listMyApp(), err: "" }); }
    catch (err) { setAppState({ status: "error", data: null, err: formatApiError(err) }); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (appState.data?.application?.status === "scoring") {
      const t = setInterval(load, 3000);
      return () => clearInterval(t);
    }
  }, [appState.data?.application?.status]);

  const onSubmit = async (payload) => {
    setBusy(true); setError("");
    try {
      await applyAmbassador(payload);
      await load(); await refresh();
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const onRescore = async () => {
    setBusy(true);
    try { await rescore(); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (appState.status === "loading") return <LoadingState label="Loading…" />;
  if (appState.status === "error") return <ErrorState detail={appState.err} />;
  const { application, eligible } = appState.data;

  return (
    <div className="space-y-8" data-testid="ambassador-community-surface">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Ambassador · My programme</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Your ambassador dashboard
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Verified Ambassadors get unique referral links tied to a 12-month, 12.5% commission window. AI recommends, admin approves.
        </p>
      </div>

      {isVerified && user?.ambassador_profile_slug && (
        <Card data-testid="ambassador-verified-card">
          <Badge variant="ambassador">Verified ambassador</Badge>
          <h2 className="font-[Montserrat] font-bold text-2xl text-[#0B1F3A] mt-3">You&apos;re in.</h2>
          <p className="mt-2 text-sm text-[#0B1F3A]/75">Your public profile is live at <b>/a/{user.ambassador_profile_slug}</b>.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/app/referrals"><Button variant="primary" data-testid="ambassador-open-referrals">Open referral ledger →</Button></Link>
            <a href={`/a/${user.ambassador_profile_slug}`} target="_blank" rel="noreferrer"><Button variant="secondary">View public profile ↗</Button></a>
          </div>
        </Card>
      )}

      {!isVerified && (
        <ApplicationStatusDashboard user={user} application={application} />
      )}

      {!isVerified && !application && eligible && (
        <Card><ApplicationForm onSubmit={onSubmit} busy={busy} error={error} /></Card>
      )}
      {!isVerified && !application && !eligible && (
        <Card data-testid="ambassador-not-eligible">
          <Badge variant="neutral">Not eligible yet</Badge>
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A] mt-3">Reach 100 reputation points first</h2>
          <p className="text-sm text-[#0B1F3A]/75 mt-2">Give qualified feedback to climb — the application form unlocks automatically once you get there.</p>
          <div className="mt-4"><Link to="/app/reputation"><Button variant="primary">Open reputation</Button></Link></div>
        </Card>
      )}
      {!isVerified && application && (
        <ApplicationDetailCard application={application} onRescore={onRescore} busy={busy} />
      )}

      {!isVerified && <LockedPreviewCard />}

      <ProductAmbassadorshipsCard enabled={isVerified} />

      <InvitationsFromFoundersCard enabled={isVerified} />
    </div>
  );
}

// ---------- Founder directory + invitations --------------------------------
function InviteForm({ ambassador, products, onDone, onCancel }) {
  const [productId, setProductId] = useState(products[0]?.id || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await inviteAmbassador({ ambassador_slug: ambassador.slug, product_id: productId || null, message });
      toast.success(`Invitation sent to ${ambassador.name}.`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3 mt-4 border-t border-[#0B1F3A]/10 pt-4" data-testid={`invite-form-${ambassador.slug}`}>
      <Field label="For which product? (optional)">
        <select
          data-testid="invite-product"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          className="w-full rounded-lg border border-[#0B1F3A]/15 bg-white px-3 py-2 text-sm text-[#0B1F3A]"
        >
          <option value="">— No specific product —</option>
          {products.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
        </select>
      </Field>
      <Field label="Message" hint="10–1200 chars. Tell them why you'd love their help.">
        <Textarea data-testid="invite-message" minLength={10} maxLength={1200} rows={4} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Hey! I loved your work on…" />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={busy || message.trim().length < 10} data-testid="invite-submit">
          {busy ? "Sending…" : "Send invitation"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="invite-cancel">Cancel</Button>
      </div>
    </form>
  );
}

function AmbassadorCard({ a, products, onInvited, alreadyInvited }) {
  const [open, setOpen] = useState(false);
  return (
    <Card data-testid={`ambassador-card-${a.slug}`} className="!p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="ambassador">Verified</Badge>
            <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/55">{a.tier}</span>
          </div>
          <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A] mt-2 truncate">{a.name}</h3>
          <div className="text-sm text-[#0B1F3A]/70">{a.points} reputation pts</div>
        </div>
        <a href={`/a/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#2D7FF9] font-bold uppercase tracking-widest whitespace-nowrap" data-testid={`ambassador-view-${a.slug}`}>Profile ↗</a>
      </div>
      {!open ? (
        <div className="mt-4">
          {alreadyInvited ? (
            <Badge variant="active" data-testid={`invited-${a.slug}`}>Already invited</Badge>
          ) : (
            <Button variant="primary" onClick={() => setOpen(true)} data-testid={`invite-btn-${a.slug}`}>Invite to promote</Button>
          )}
        </div>
      ) : (
        <InviteForm
          ambassador={a}
          products={products}
          onDone={() => { setOpen(false); onInvited(); }}
          onCancel={() => setOpen(false)}
        />
      )}
    </Card>
  );
}

function FounderDirectorySurface() {
  const [state, setState] = useState({ status: "loading", err: "" });
  const [ambassadors, setAmbassadors] = useState([]);
  const [products, setProducts] = useState([]);
  const [sent, setSent] = useState([]);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const [amb, prods, s] = await Promise.all([publicList(), listOwnProducts(), listSentInvitations()]);
      setAmbassadors(amb || []);
      setProducts(prods || []);
      setSent(s || []);
      setState({ status: "ready", err: "" });
    } catch (err) { setState({ status: "error", err: formatApiError(err) }); }
  };
  useEffect(() => { load(); }, []);

  const invitedSlugs = useMemo(() =>
    new Set(sent.filter((s) => s.status === "sent").map((s) => s.ambassador?.slug).filter(Boolean)),
    [sent]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ambassadors;
    return ambassadors.filter((a) => a.name?.toLowerCase().includes(q) || a.slug?.toLowerCase().includes(q));
  }, [query, ambassadors]);

  if (state.status === "loading") return <LoadingState label="Loading ambassadors…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  return (
    <div data-testid="ambassadors-founder-surface" className="space-y-8">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Founders · Ambassadors</p>
        <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">
          Find ambassadors to test and promote your app
        </h1>
        <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">
          Verified ambassadors have qualified reputation and a public profile. Send a personal
          invitation for one of your products — if they accept, their referral link earns them
          <b> 12.5% for 12 months</b> on every subscription they bring you.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-6">
        <div className="flex-1">
          <Field label="Search ambassadors by name or slug">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. tester-2" data-testid="ambassadors-search" />
          </Field>
        </div>
        <div className="text-sm text-[#0B1F3A]/70">
          <b className="text-[#0B1F3A]">{filtered.length}</b> ambassador{filtered.length === 1 ? "" : "s"} available
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No matches" description="Try clearing the search or come back once more ambassadors are verified." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ambassadors-directory">
          {filtered.map((a) => (
            <AmbassadorCard
              key={a.slug}
              a={a}
              products={products}
              onInvited={load}
              alreadyInvited={invitedSlugs.has(a.slug)}
            />
          ))}
        </div>
      )}

      <Card data-testid="founder-invitations-sent">
        <div className="flex items-center justify-between">
          <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">Invitations you sent</h2>
          <span className="text-xs text-[#0B1F3A]/60">{sent.length} total</span>
        </div>
        {sent.length === 0 ? (
          <p className="text-sm text-[#0B1F3A]/60 mt-2">No invitations yet — pick an ambassador above and reach out.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 border-b border-[#0B1F3A]/10">
                  <th className="py-2 pr-3">Ambassador</th>
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Sent</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {sent.map((s) => {
                  const meta = INV_BADGE[s.status] || INV_BADGE.sent;
                  return (
                    <tr key={s.id} className="border-b border-[#0B1F3A]/5">
                      <td className="py-2 pr-3 text-[#0B1F3A]/85">
                        {s.ambassador?.slug ? (<a href={`/a/${s.ambassador.slug}`} target="_blank" rel="noreferrer" className="text-[#2D7FF9]">{s.ambassador.name || s.ambassador.slug}</a>) : "—"}
                      </td>
                      <td className="py-2 pr-3 text-[#0B1F3A]/70">{s.product?.name || <span className="text-[#0B1F3A]/40">—</span>}</td>
                      <td className="py-2 pr-3 text-[#0B1F3A]/70">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td className="py-2 pr-3"><Badge variant={meta.variant}>{meta.label}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------- Entry component ------------------------------------------------
export default function Ambassadors() {
  const { user, refresh } = useAuth();
  if (!user) return null;

  const isFounder = user.roles?.includes("founder");
  const isCommunity = user.roles?.includes("community");

  // A founder cannot join the ambassador program (conflict of interest) — route them to
  // the discovery + invitation surface instead.
  if (isFounder) return <FounderDirectorySurface />;
  if (isCommunity) return <CommunityAmbassadorSurface user={user} refresh={refresh} />;

  return (
    <div data-testid="ambassadors-no-access">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Ambassador programme</p>
      <h1 className="font-[Montserrat] font-extrabold text-2xl text-[#0B1F3A] mt-2">Add a role from Settings to see this workspace.</h1>
    </div>
  );
}
